import { useEffect, useState } from "react";

/**
 * Current time, ticking on minute (or second) boundaries. One timer per
 * component, aligned to the boundary so clocks never drift visibly.
 */
export function useNow(granularity: "minute" | "second" = "minute"): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let timer: number;
    const unit = granularity === "second" ? 1000 : 60_000;
    const schedule = () => {
      const ms = unit - (Date.now() % unit) + 20;
      timer = window.setTimeout(() => {
        setNow(new Date());
        schedule();
      }, ms);
    };
    schedule();
    // Catch up immediately when a sleeping display wakes or the tab returns.
    const onVisible = () => document.visibilityState === "visible" && setNow(new Date());
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [granularity]);
  return now;
}

export function relativeAge(fromIso: string | number, now: Date = new Date()): string {
  const ms = now.getTime() - (typeof fromIso === "number" ? fromIso : Date.parse(fromIso));
  const min = Math.round(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} hr${h > 1 ? "s" : ""} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d > 1 ? "s" : ""} ago`;
}

export function formatTime(d: Date, timeZone: string, h24: boolean): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit", hour12: !h24 }).format(d);
}

/** Compact time like "5:30p" / "17:30" for dense calendar cells. */
export function formatShortTime(d: Date, timeZone: string, h24: boolean): string {
  if (h24) return formatTime(d, timeZone, true);
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit", hour12: true }).formatToParts(d);
  const hour = parts.find((p) => p.type === "hour")?.value ?? "";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
  const ampm = (parts.find((p) => p.type === "dayPeriod")?.value ?? "").toLowerCase().charAt(0);
  return `${hour}${minute === "00" ? "" : `:${minute}`}${ampm}`;
}
